import { useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  type BidirectionalOptions,
  type LegacyOptions,
  MessageTypes,
  type NativeMessage,
  type NativeToWebRequest,
  type NativeToWebResponse,
  type PendingRequest,
  type RequestHandler,
  type TNativeAction,
  type TNativeToWebAction,
  type TWebToNativeEvent,
  type TWebToNativeRequest,
  type WebToNativeEvent,
  type WebToNativeRequest,
  type WebToNativeResponse,
  nativeToWebActionNames,
  webToNativeActionEventNames,
  webToNativeRequestNames,
} from '../types/index.types';

import { EXTERNAL_NATIVE_APPLICATIONS } from '@/config/EnviromentConfig';
import useMsalHelper from '@/hooks/useMsalHelper';

const useNativeAction = (action?: TNativeAction, legacyOptions?: LegacyOptions) => {
  // const { addRequest, addResponse, addLog } = useContext(EnvironmentContext);

  // Use centralized MSAL helper for user info
  const { getUserInfo } = useMsalHelper();

  // Track outgoing requests (web → native)
  const pendingRequests = useRef<Map<string, PendingRequest>>(new Map());

  // Track incoming request handlers (native → web)
  const requestHandlers = useRef<Map<string, RequestHandler>>(new Map());

  // Track incoming event listeners (native → web events)
  const eventListeners = useRef<Map<string, Set<(payload: unknown) => void>>>(new Map());

  // Queue for incoming requests that arrive before handlers are registered
  const queuedRequests = useRef<
    Map<string, { request: NativeToWebRequest; timestamp: number; timeoutId: ReturnType<typeof setTimeout> }>
  >(new Map());
  const REQUEST_QUEUE_TIMEOUT = 10000; // 10 seconds

  // Track which requests have already received responses (prevent duplicates)
  const respondedRequests = useRef<Set<string>>(new Set());

  // Legacy support - single request tracking
  const latestRequest = useRef<string | undefined>(undefined);

  // Send message to native platform
  const sendToNative = useCallback((message: NativeMessage, options?: BidirectionalOptions) => {
    // Debug log before sending
    let webToNativeMsgRaw: string;

    if (window?.webkit?.messageHandlers?.appInterface) {
      const iosMessage = { ...message, channel: 'ios' as const };
      webToNativeMsgRaw = JSON.stringify(iosMessage);
      window.webkit.messageHandlers.appInterface.postMessage(iosMessage);
    } else if (window.androidApp) {
      const androidMessage = { ...message, channel: 'android' as const };
      webToNativeMsgRaw = JSON.stringify(androidMessage);
      window.androidApp.postMessage(webToNativeMsgRaw);
    } else {
      if (options?.onCancelAction) {
        options.onCancelAction();
      }
      return false;
    }

    // addRequest(webToNativeMsgRaw);
    // addLog(webToNativeMsgRaw);
    options?.onTrigger?.(webToNativeMsgRaw);
    return true;
  }, []);

  // Send a request and wait for response (web → native)
  const sendWebToNativeRequest = useCallback(
    async (actionName: TWebToNativeRequest, payload?: unknown, options?: BidirectionalOptions): Promise<unknown> => {
      const requestId = uuidv4();
      const timeout = options?.requestTimeout || 30000;
      const startTime = Date.now();

      // Get user info outside Promise executor
      const userInfo = await getUserInfo();

      return new Promise((resolve, reject) => {
        // Store the pending request
        pendingRequests.current.set(requestId, {
          action: actionName,
          onSuccess: (event) => {
            resolve(event.data);
          }, // Return the full response, not just payload
          onError: (err) => {
            reject(new Error(err));
          },
          timestamp: startTime,
        });

        // Set timeout for the request
        setTimeout(() => {
          if (pendingRequests.current.has(requestId)) {
            pendingRequests.current.delete(requestId);
            reject(new Error(`Request timeout after ${timeout}ms`));
          }
        }, timeout);

        const message: WebToNativeRequest = {
          type: MessageTypes.WEB_TO_NATIVE_REQUEST,
          name: webToNativeRequestNames[actionName],
          requestId,
          payload,
          channel: 'ios', // Will be overridden in sendToNative
          userInfo,
        };

        const sent = sendToNative(message, options);
        if (!sent) {
          pendingRequests.current.delete(requestId);
          reject(new Error('Failed to send message to native'));
        }
      });
    },
    [sendToNative, getUserInfo]
  );

  // Send a response to a native request (web → native)
  const respondToNativeRequest = useCallback(
    async (
      requestId: string,
      responseData: unknown,
      success: boolean,
      error?: string,
      requestName?: string,
      options?: BidirectionalOptions
    ) => {
      const message: WebToNativeResponse = {
        type: MessageTypes.WEB_TO_NATIVE_RESPONSE,
        name: requestName || 'response', // Use original request name if provided
        requestId: uuidv4(),
        responseToRequestId: requestId,
        success,
        error,
        payload: responseData,
        channel: 'ios', // Will be overridden in sendToNative
        userInfo: await getUserInfo(),
      };

      return sendToNative(message, options);
    },
    [sendToNative, getUserInfo]
  );

  // Send a response with deduplication check (ensures only one response per request)
  const respondToNativeRequestOnce = useCallback(
    (
      requestId: string,
      responseData: unknown,
      success: boolean,
      error?: string,
      requestName?: string,
      options?: BidirectionalOptions
    ) => {
      // Check if we've already responded to this request
      if (respondedRequests.current.has(requestId)) {
        console.warn(`[Native Bridge] Already responded to request ${requestId}, skipping duplicate response`);
        return false;
      }

      // Mark this request as responded
      respondedRequests.current.add(requestId);

      // Send the response
      return respondToNativeRequest(requestId, responseData, success, error, requestName, options);
    },
    [respondToNativeRequest]
  );

  // Register a handler for native → web requests
  const registerRequestHandler = useCallback(
    (actionName: TNativeToWebAction, handler: (payload: unknown, requestId: string) => Promise<unknown> | unknown) => {
      const actionKey = nativeToWebActionNames[actionName];
      requestHandlers.current.set(actionKey, { handler });

      // Process any queued requests for this action
      const queuedRequestsToProcess: Array<{
        request: NativeToWebRequest;
        timestamp: number;
        timeoutId: ReturnType<typeof setTimeout>;
      }> = [];
      queuedRequests.current.forEach((queuedItem, key) => {
        if (queuedItem.request.name === actionKey) {
          queuedRequestsToProcess.push(queuedItem);
          queuedRequests.current.delete(key);
        }
      });

      // Process queued requests
      queuedRequestsToProcess.forEach(({ request, timeoutId }) => {
        // Cancel the timeout since we're processing the request now
        clearTimeout(timeoutId);

        try {
          const result = handler(request.payload, request.requestId);

          Promise.resolve(result)
            .then((responseData) => {
              respondToNativeRequestOnce(request.requestId, responseData, true, undefined, request.name);
            })
            .catch((error) => {
              respondToNativeRequestOnce(request.requestId, null, false, error.message, request.name);
            });
        } catch (error) {
          respondToNativeRequestOnce(request.requestId, null, false, (error as Error).message, request.name);
        }
      });

      return () => {
        requestHandlers.current.delete(actionKey);
      };
    },
    [respondToNativeRequestOnce]
  );

  // Register a listener for native → web events
  const registerEventListener = useCallback((actionName: TNativeToWebAction, listener: (payload: unknown) => void) => {
    const actionKey = nativeToWebActionNames[actionName];

    if (!eventListeners.current.has(actionKey)) {
      eventListeners.current.set(actionKey, new Set());
    }
    eventListeners.current.get(actionKey)?.add(listener);
    return () => {
      eventListeners.current.get(actionKey)?.delete(listener);
      if (eventListeners.current.get(actionKey)?.size === 0) {
        eventListeners.current.delete(actionKey);
      }
    };
  }, []);

  // Send an event (fire-and-forget) to native
  const sendWebToNativeEvent = useCallback(
    async (actionName: TWebToNativeEvent, payload?: unknown, options?: BidirectionalOptions) => {
      const message: WebToNativeEvent = {
        type: MessageTypes.WEB_TO_NATIVE_EVENT,
        name: webToNativeActionEventNames[actionName],
        requestId: uuidv4(),
        payload,
        channel: 'ios', // Will be overridden in sendToNative
        userInfo: await getUserInfo(),
      };

      return sendToNative(message, options);
    },
    [sendToNative, getUserInfo]
  );

  // Legacy runAction method (maintains backward compatibility)
  const runAction = useCallback(
    async (payload?: object) => {
      if (!action || (!payload && !legacyOptions)) {
        console.warn('runAction called without action or options');
        return;
      }

      const requestId = uuidv4();
      latestRequest.current = requestId;

      // Determine if this is an event or request
      const isEvent = action in webToNativeActionEventNames;
      const actionName = isEvent
        ? webToNativeActionEventNames[action as TWebToNativeEvent]
        : webToNativeRequestNames[action as TWebToNativeRequest];

      const message: WebToNativeEvent = {
        type: MessageTypes.WEB_TO_NATIVE_EVENT,
        name: actionName,
        requestId,
        payload,
        channel: 'ios', // Will be overridden in sendToNative
        userInfo: await getUserInfo(),
      };

      sendToNative(message, {
        onTrigger: legacyOptions?.onTrigger,
        onCancelAction: legacyOptions?.onCancelAction,
      });
    },
    [action, legacyOptions, sendToNative, getUserInfo]
  );

  // Handle incoming messages from native
  useEffect(() => {
    const messageEventCallback = (event: MessageEvent) => {
      const receiveTime = Date.now();

      const response = JSON.stringify(event.data);
      // addResponse(response);
      legacyOptions?.onResponse?.(response);

      event.preventDefault();

      // Validate applicationId
      if (!EXTERNAL_NATIVE_APPLICATIONS.includes(event.data.applicationId)) {
        return;
      }

      const messageData = event.data as NativeMessage;

      switch (messageData.type) {
        case MessageTypes.NATIVE_TO_WEB_RESPONSE: {
          // Handle response to web → native request
          const responseMsg: NativeToWebResponse = messageData;
          const pendingRequest = pendingRequests.current.get(responseMsg.responseToRequestId);

          if (pendingRequest) {
            pendingRequests.current.delete(responseMsg.responseToRequestId);

            if (responseMsg.success) {
              pendingRequest.onSuccess(event);
            } else {
              pendingRequest.onError(responseMsg.error || 'Unknown error');
            }
          }
          break;
        }

        case MessageTypes.NATIVE_TO_WEB_REQUEST: {
          // Handle native → web request
          const requestMsg: NativeToWebRequest = messageData;
          const handler = requestHandlers.current.get(requestMsg.name);

          if (handler) {
            try {
              const result = handler.handler(requestMsg.payload, requestMsg.requestId);

              // Handle both sync and async handlers
              Promise.resolve(result)
                .then((responseData) => {
                  respondToNativeRequestOnce(requestMsg.requestId, responseData, true, undefined, requestMsg.name);
                })
                .catch((error) => {
                  respondToNativeRequestOnce(requestMsg.requestId, null, false, error.message, requestMsg.name);
                });
            } catch (error) {
              respondToNativeRequestOnce(requestMsg.requestId, null, false, (error as Error).message, requestMsg.name);
            }
          } else {
            // Queue the request if no handler is registered yet
            // Set a timeout to clean up this queued request if handler never registers
            const timeoutId = setTimeout(() => {
              if (queuedRequests.current.has(requestMsg.requestId)) {
                queuedRequests.current.delete(requestMsg.requestId);
                respondToNativeRequestOnce(
                  requestMsg.requestId,
                  null,
                  false,
                  `No handler registered for action: ${requestMsg.name} (timeout after ${REQUEST_QUEUE_TIMEOUT}ms)`,
                  requestMsg.name
                );
              }
            }, REQUEST_QUEUE_TIMEOUT);

            queuedRequests.current.set(requestMsg.requestId, {
              request: requestMsg,
              timestamp: receiveTime,
              timeoutId,
            });
          }
          break;
        }
        case MessageTypes.NATIVE_TO_WEB_EVENT:
        default: {
          // Handle native → web events
          const eventMsg = messageData;
          const eventName = eventMsg.name;

          // Call all registered listeners for this event
          const listeners = eventListeners.current.get(eventName);
          if (listeners && listeners.size > 0) {
            listeners.forEach((listener) => {
              try {
                listener(eventMsg.payload);
              } catch (error) {
                console.error(`[Native Bridge] Error in event listener for ${eventName}:`, error);
              }
            });
          }

          // Handle legacy events and new events
          // Legacy compatibility check
          if (action) {
            const expectedName =
              action in webToNativeActionEventNames
                ? webToNativeActionEventNames[action as TWebToNativeEvent]
                : webToNativeRequestNames[action as TWebToNativeRequest];

            if (event.data.action === expectedName) {
              if (event.data.requestId === latestRequest.current) {
                legacyOptions?.onSuccess?.(event);
              }
            }
          }
          break;
        }
      }
    };

    window.addEventListener('message', messageEventCallback);
    return () => {
      window.removeEventListener('message', messageEventCallback);
    };
  }, [action, legacyOptions, respondToNativeRequestOnce]);

  // Cleanup pending requests and queued requests on unmount
  useEffect(() => {
    // Capture current refs in closure
    const pending = pendingRequests.current;
    const handlers = requestHandlers.current;
    const queued = queuedRequests.current;
    const responded = respondedRequests.current;

    return () => {
      // Cancel all pending timeouts
      queued.forEach(({ timeoutId }) => {
        clearTimeout(timeoutId);
      });

      pending.clear();
      handlers.clear();
      queued.clear();
      responded.clear();
    };
  }, []);

  // Return both legacy and new APIs
  return {
    // Legacy API (backward compatibility)
    runAction,

    // New bidirectional API
    sendRequestToNative: sendWebToNativeRequest,
    sendResponseToNative: respondToNativeRequest,
    sendEventToNative: sendWebToNativeEvent,
    registerRequestHandler,
    registerEventListener,
  };
};

export default useNativeAction;
